import React, { useState, useRef } from 'react'
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  position: relative;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  
  @media (min-width: 768px) {
    width: 50%;
    height: 100vh;
    position: relative;
    z-index: 1;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 0;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const LoginSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  position: relative;
  z-index: 1;
  padding: 20px;
  
  @media (min-width: 768px) {
    width: 50%;
    padding: 40px;
  }
`;

const LoginCard = styled.div`
  background: linear-gradient(to bottom right, 
    rgba(253, 242, 248, 0.55) 0%, 
    rgba(252, 231, 243, 0.55) 50%, 
    rgba(250, 245, 255, 0.65) 100%
  );
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  filter: drop-shadow(0 0 16px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 16px rgba(255, 255, 255, 0.6));
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  object-fit: contain;
`;

const Title = styled.h1`
  margin-bottom: 10px;
  color: #333;
  font-size: 24px;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
  font-size: 14px;
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 1);
  border: 2px solid #e1e1e1; 
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  backdrop-filter: none;
  
  &:hover:not(:disabled) {
    border-color: #4285f4;
    box-shadow: 0 2px 4px rgba(66, 133, 244, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleIcon = styled.svg`
  width: 20px;
  height: 20px;
`;

const EmailFormContainer = styled.div`
  margin: 24px 0;
  padding: 24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputField = styled.input`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: black;
  font-size: 16px;
  font-family: 'Assistant', sans-serif;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(92, 87, 87, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const EmailButton = styled.button`
  background: rgba(255, 255, 255, 1);
border: 2px solid #e1e1e1;  border-radius: 12px;
  padding: 12px 24px;
  color: black;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Assistant', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 14px;
`;

const LoadingText = styled.span`
  color: #666;
`;

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  const { signInWithGoogle, signInWithEmail, signUpWithEmail, error, authorizationLoading } = useAuth();

  const handleGoogleSignIn = async () => {
    setIsLoading(true);

    try {
      await signInWithGoogle();
      // Success - AuthContext will handle state update and authorization check
    } catch (err) {
      console.error('Google sign-in failed:', err);
      // Error is already set in AuthContext
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setEmailLoading(true);
    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      console.error('Email authentication failed:', err);
    } finally {
      setEmailLoading(false);
    }
  };


  // Show loading state during authorization check
  const isProcessing = isLoading || authorizationLoading;
  const videoUrl = "/themeVid.mp4";
  const videoRef = useRef(null);

  return (
    <LoginContainer>
      <BackgroundVideo
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        src={videoUrl}
      >
        Your browser does not support the video tag.
      </BackgroundVideo>
      <VideoOverlay />
      <LoginSection>
        <LogoContainer>
          <Logo
            src="/clinic-q-logo.png"
            alt="BQS Logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </LogoContainer>
        <LoginCard>
          <Title>התחברות למערכת</Title>
          <Subtitle>התחבר למערכת עם חשבון הגוגל שברשותך</Subtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <GoogleButton onClick={handleGoogleSignIn} disabled={isProcessing}>
            {isProcessing ? (
              <LoadingText>
                {authorizationLoading ? 'בודק הרשאות...' : 'מתחבר...'}
              </LoadingText>
            ) : (
              <>
                <GoogleIcon viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </GoogleIcon>
                בצע התחברות עם גוגל
              </>
            )}
          </GoogleButton>

          <EmailFormContainer>
            <div className="flex justify-between items-center mb-4">
              <h3>{isSignUp ? 'הרשמה באמצעות מייל' : 'התחברות באמצעות מייל'}</h3>
              <button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors underline"
              >
                {isSignUp ? 'יש לך כבר חשבון? התחבר' : 'אין לך חשבון? הירשם'}
              </button>
            </div>
            <EmailForm onSubmit={handleEmailAuth}>
              <InputField
                type="email"
                placeholder="כתובת מייל"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={emailLoading || isProcessing}
              />
              <InputField
                type="password"
                placeholder="סיסמה"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
                disabled={emailLoading || isProcessing}
              />
              <EmailButton
                type="submit"
                disabled={emailLoading || isProcessing || !email || !password}
              >
                {emailLoading ? 'מתבצע...' : (isSignUp ? 'הירשם' : 'התחבר')}
              </EmailButton>
            </EmailForm>
          </EmailFormContainer>
        </LoginCard>
      </LoginSection>
    </LoginContainer>
  );
};

export default Login;